import React, { useState } from 'react'
import {
  makeStyles,
  Grid,
  Card,
  Alert,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  InputField,
  Button
} from '@paintscout/ui'
import type { DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'

import CloseIcon from '@material-ui/icons/Close'
import type { QuoteJobAddress } from 'paintscout'
import { getQuoteOptions } from '@paintscout/util/builder'
import { useQuote } from '../../context'
import { useAddressSearch } from '../../../../../../apps/shared/hooks/useAddressSearch'

const useStyles = makeStyles((_theme: Theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))

export interface JobAddressDialogProps extends DialogProps {
  jobAddress?: QuoteJobAddress
  jobIdentifier?: string
  onConfirm?: ({
    address,
    province,
    city,
    postal,
    identifier
  }: {
    address: string
    province: string
    city: string
    postal: string
    identifier: string
  }) => void
  onCancel?: () => void
}

function JobAddressDialog(props: JobAddressDialogProps) {
  const { jobAddress, jobIdentifier, onConfirm, onCancel, loading, ...baseDialogProps } = props
  const classes = useStyles()

  const [address, setAddress] = useState(jobAddress?.address ?? '')
  const [province, setProvince] = useState(jobAddress?.province ?? '')
  const [city, setCity] = useState(jobAddress?.city ?? '')
  const [postal, setPostal] = useState(jobAddress?.postal ?? '')
  const [identifier, setIdentifier] = useState(jobIdentifier ?? '')
  const [addressOpen, setAddressOpen] = useState(false)

  const { quote } = useQuote()
  const { alwaysShowJobAddress } = getQuoteOptions({ quote })
  const { options } = useClientOptions()

  const { addressSearchResults, debouncedAddressSearch } = useAddressSearch({
    options: options as any
  })

  // The label "Use Contact Address" stamps on. We compare the identifier's *value* against it rather
  // than tracking a flag set this session, so the rule still holds when the user reopens the dialog
  // later to change the address. A hand-typed identifier never matches, so it survives. (ENG-1095 QA)
  // Both sides get trimmed. A first name stored as " John " would otherwise build a label that
  // never matches the value it wrote, and the stale auto label would survive every address change.
  const contactAddressLabel = `${quote?.contact?.first_name?.trim() || 'Contact'}'s Address`

  // The identifier belongs to the address it was generated for. Once the address moves, an
  // auto-generated contact label is wrong — drop it instead of leaving "JP's Address" on a
  // different street. (ENG-1095 QA)
  const clearAutoIdentifier = () => {
    if (identifier.trim() === contactAddressLabel) {
      setIdentifier('')
    }
  }

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setAddressOpen(true)
    debouncedAddressSearch(value)
    setAddress(value)
    clearAutoIdentifier()
  }

  const handleAddressSelect = (result: any) => {
    setAddressOpen(false)
    const { municipality, postalCode, region, address: resultAddress } = result
    setAddress(resultAddress ?? address)
    setCity(municipality ?? city)
    setProvince(region ?? province)
    setPostal(postalCode ?? postal)
    clearAutoIdentifier()
  }

  const handleCopyAddress = () => {
    const contactAddress = {
      address: quote?.contact?.address ?? '',
      city: quote?.contact?.city ?? '',
      province: quote?.contact?.province ?? '',
      postal: quote?.contact?.postal ?? ''
    }
    setAddress(contactAddress.address)
    setCity(contactAddress.city)
    setProvince(contactAddress.province)
    setPostal(contactAddress.postal)
    // A contact with no address used to still get the label, which left the header showing
    // "JP's Address" and nothing else. (ENG-1095 QA)
    setIdentifier(isBlankAddress(contactAddress) ? '' : contactAddressLabel)
  }

  return (
    <Dialog open={true} maxWidth="md" {...baseDialogProps}>
      <DialogTitle
        loading={loading}
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        Edit Job Address
      </DialogTitle>
      <DialogContent>
        <Grid container={true} spacing={4}>
          <Grid item={true} xs={12}>
            <InputField
              label="Identifier"
              fullWidth={true}
              value={identifier}
              onChange={(ev) => setIdentifier(ev.target.value)}
            />
          </Grid>
          {alwaysShowJobAddress && (
            <Grid item xs={12}>
              <Alert
                severity="warning"
                title="Always Show Job Address"
                content="If no address is listed below, we'll show the contact's address instead."
              />
            </Grid>
          )}
          <Grid item={true} xs={12} style={{ position: 'relative' }}>
            <InputField
              label="Address"
              placeholder="Enter address"
              fullWidth={true}
              value={address}
              onChange={handleAddressChange}
              onClick={() => setAddressOpen(true)}
              onBlur={() => setAddressOpen(false)}
              autoComplete="off"
              inputProps={{
                role: 'presentation'
              }}
            />
            {addressSearchResults.length > 0 && addressOpen && (
              <Card
                style={{
                  zIndex: 21474899999,
                  position: 'absolute',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                  width: '97%',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}
              >
                {addressSearchResults.map((result, i) => {
                  const label = [result.address, result.municipality].filter((item) => item).join(', ')
                  const label2 = [result.region, result.postalCode].filter((item) => item).join(', ')

                  return (
                    <div
                      key={i}
                      onMouseDown={() => handleAddressSelect(result)}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                      }}
                    >
                      <Typography variant="body1">{label}</Typography>
                      <Typography variant="subtitle2">{label2}</Typography>
                    </div>
                  )
                })}
              </Card>
            )}
          </Grid>
          <Grid item={true} xs={12} sm={4}>
            <InputField
              label="City"
              fullWidth={true}
              placeholder="Enter city"
              value={city}
              onChange={(ev) => setCity(ev.target.value)}
            />
          </Grid>
          <Grid item={true} xs={12} sm={4}>
            <InputField
              label="State / Province"
              fullWidth={true}
              placeholder="Enter state / province"
              inputProps={{ maxLength: 2 }}
              value={province}
              onChange={(ev) => setProvince(ev.target.value)}
            />
          </Grid>
          <Grid item={true} xs={12} sm={4}>
            <InputField
              label="Zip / Postal"
              fullWidth={true}
              placeholder="Enter ZIP / postal code"
              value={postal}
              onChange={(ev) => setPostal(ev.target.value)}
            />
          </Grid>
          {quote?.contact && (
            <Grid item xs={12}>
              <Grid container={true} spacing={1}>
                <Grid item={true} xs={12}>
                  <Button type="submit" variant={'dialog'} onClick={handleCopyAddress}>
                    Use Contact Address
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    *Will override current input. If there isn’t a contact address, your fields will be blank.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button type="submit" variant={'contained'} onClick={handleConfirm} disabled={loading}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  function handleConfirm() {
    // Only the auto-generated contact-address label is tied to having an address behind it — a
    // hand-typed identifier is allowed to stand alone with no address (#23004).
    const isBlank = isBlankAddress({ address, province, city, postal })
    const isAutoLabel = identifier.trim() === contactAddressLabel
    onConfirm({ address, province, city, postal, identifier: isBlank && isAutoLabel ? '' : identifier })
  }
}

/**
 * True when every part of the address is empty.
 *
 * `apps/app`'s `isEmptyAddress` does the same job, but this dialog lives in a package and can't
 * import from the app.
 */
function isBlankAddress(jobAddress: Partial<QuoteJobAddress>): boolean {
  return ![jobAddress.address, jobAddress.city, jobAddress.province, jobAddress.postal].some((value) => !!value?.trim())
}

export default JobAddressDialog
